// Hook that manages the browser's Pro entitlement for the (login-less) app.
//
// Flow:
//   1. On mount, if the URL has ?checkout=success&session_id=..., persist that
//      session id to localStorage (Stripe just redirected us back) and clean the
//      URL.
//   2. Look up the stored session id against GET /entitlement to hydrate the
//      current feature set. Falls back to free features if anything is missing.
//   3. startCheckout(mode) POSTs to /checkout and redirects to Stripe.
import { useState, useEffect, useCallback } from 'react';
import {
  PRO_API_URL,
  PRO_ENABLED,
  ENTITLEMENT_STORAGE_KEY,
  FREE_FEATURES,
} from './proConfig';

function readStoredSessionId() {
  try {
    return window.localStorage.getItem(ENTITLEMENT_STORAGE_KEY);
  } catch {
    return null;
  }
}

function storeSessionId(id) {
  try {
    window.localStorage.setItem(ENTITLEMENT_STORAGE_KEY, id);
  } catch {
    /* ignore quota / privacy-mode errors */
  }
}

export default function useEntitlement() {
  const [features, setFeatures] = useState(FREE_FEATURES);
  const [plan, setPlan] = useState(null);
  const [isPro, setIsPro] = useState(false);
  const [loading, setLoading] = useState(PRO_ENABLED);
  const [justUpgraded, setJustUpgraded] = useState(false);

  // Capture the Stripe redirect (?checkout=success&session_id=...) once.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkout = params.get('checkout');
    const sessionId = params.get('session_id');
    if (checkout === 'success' && sessionId) {
      storeSessionId(sessionId);
      setJustUpgraded(true);
    }
    if (checkout) {
      // Clean the query params so a refresh doesn't re-trigger.
      params.delete('checkout');
      params.delete('session_id');
      const clean =
        window.location.pathname + (params.toString() ? `?${params}` : '');
      window.history.replaceState({}, '', clean);
    }
  }, []);

  // Hydrate entitlement from the backend.
  useEffect(() => {
    if (!PRO_ENABLED) {
      setLoading(false);
      return;
    }
    const sessionId = readStoredSessionId();
    if (!sessionId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${PRO_API_URL}/entitlement?session_id=${encodeURIComponent(sessionId)}`
        );
        const data = await res.json();
        if (cancelled) return;
        if (data && data.active) {
          setFeatures({ ...FREE_FEATURES, ...data.features });
          setPlan(data.plan);
          setIsPro(true);
        }
      } catch (err) {
        // Network / backend hiccup: silently stay on the free tier.
        console.warn('Entitlement lookup failed:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const startCheckout = useCallback(async (mode = 'subscription') => {
    if (!PRO_ENABLED) return { ok: false, reason: 'not-configured' };
    try {
      const res = await fetch(`${PRO_API_URL}/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode }),
      });
      const data = await res.json();
      if (data && data.url) {
        window.location.href = data.url;
        return { ok: true };
      }
      return { ok: false, reason: data.error || 'no-url' };
    } catch (err) {
      console.error('Checkout failed:', err);
      return { ok: false, reason: 'network' };
    }
  }, []);

  return {
    features,
    plan,
    isPro,
    loading,
    justUpgraded,
    dismissUpgraded: () => setJustUpgraded(false),
    startCheckout,
  };
}
